import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import {
  Checkbox,
  COLUMN_GROUP_NATIVE_TYPE,
  InlineFormattedNumber,
  NumberVariants,
  ProductTypeEnum,
  type ColumnDef,
  type ColumnGroup,
  type ColumnOrColumnGroup,
  type Leaves,
  type Security,
  type UnifiedLiquidityToken,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { compact, uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import {
  hasUnifiedLiquiditySelection,
  toggleUnifiedLiquiditySelections,
} from '../../MarketSelector/utils/unifiedLiquidity';
import type { UnifiedLiquidityMarketSelection } from '../types';
import type { MaoRow } from './MaoRow';

interface UseColumnsProps {
  baseCurrency: string;
  quoteCurrency: string;
  unifiedLiquidityTokens?: UnifiedLiquidityToken[];
  securitiesBySymbol: Map<string, Security>;
  selectedUnifiedLiquidityMarkets?: UnifiedLiquidityMarketSelection[];
  onChangeUnifiedLiquidityMarkets?: (selections: UnifiedLiquidityMarketSelection[]) => void;
}

const EMPTY_UNIFIED_LIQUIDITY_SELECTIONS: UnifiedLiquidityMarketSelection[] = [];

export const useColumns = ({
  baseCurrency,
  quoteCurrency,
  unifiedLiquidityTokens,
  securitiesBySymbol,
  selectedUnifiedLiquidityMarkets = EMPTY_UNIFIED_LIQUIDITY_SELECTIONS,
  onChangeUnifiedLiquidityMarkets,
}: UseColumnsProps): { columns: ColumnOrColumnGroup[]; unifiedLiquidityColumns: UnifiedLiquidityColumn[] } => {
  const { canViewCreditAndBalances } = useFeatureFlag();

  const numericColum = (field: Leaves<MaoRow>, currencyField: keyof MaoRow, title: string, hide = false) => {
    return {
      type: 'custom',
      field,
      title,
      params: {
        hide,
        headerClass: 'ag-right-aligned-header',
        cellClass: 'ag-right-aligned-cell',
        cellRenderer: (params: ICellRendererParams) => {
          const currency = params.data?.[currencyField] ?? '';
          const currencyIncrement = params.data?.[`${currencyField}Increment`];
          return params.value && params.value !== '0' ? (
            <InlineFormattedNumber
              variant={params.value < 0 ? NumberVariants.Negative : undefined}
              number={params.value}
              currency={currency}
              increment={currencyIncrement}
            />
          ) : (
            '-'
          );
        },
      },
    } satisfies ColumnDef<MaoRow>;
  };

  const unifiedLiquidityColumns = useMemo(
    () => getUnifiedLiquidityColumns(unifiedLiquidityTokens ?? [], securitiesBySymbol),
    [securitiesBySymbol, unifiedLiquidityTokens]
  );

  const columns = useMemo(() => {
    const hasUnifiedLiquidityColumns = unifiedLiquidityColumns.length > 0 && !!onChangeUnifiedLiquidityMarkets;
    const defaultColumns = compact([
      {
        type: 'text',
        field: 'type',
        hide: true,
        rowGroup: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      hasUnifiedLiquidityColumns && {
        type: 'text',
        field: 'marketGroup',
        hide: true,
        rowGroup: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      {
        type: 'text',
        field: 'name',
        width: 200,
        title: 'Name',
        hide: true,
        frozen: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      {
        type: 'marketColor',
        field: 'market',
        frozen: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      {
        type: 'filler',
        id: 'filler',
      },
      canViewCreditAndBalances && numericColum('unsettledBase', 'baseCurrency', `Unsettled ${baseCurrency}`),
      canViewCreditAndBalances && numericColum('unsettledQuote', 'quoteCurrency', `Unsettled ${quoteCurrency}`),
      canViewCreditAndBalances && numericColum('maxBuyBase', 'baseCurrency', `Max Buy ${baseCurrency}`),
      canViewCreditAndBalances && numericColum('maxBuyQuote', 'quoteCurrency', `Max Buy ${quoteCurrency}`, true),
      canViewCreditAndBalances && numericColum('maxSellBase', 'baseCurrency', `Max Sell ${baseCurrency}`),
      canViewCreditAndBalances && numericColum('maxSellQuote', 'quoteCurrency', `Max Sell ${quoteCurrency}`, true),
    ]) satisfies ColumnDef<MaoRow>[];

    if (!hasUnifiedLiquidityColumns) {
      return defaultColumns;
    }

    return [
      ...defaultColumns,
      ...getUnifiedLiquidityColumnGroups({
        unifiedLiquidityColumns,
        selectedUnifiedLiquidityMarkets,
        onChangeUnifiedLiquidityMarkets,
      }),
    ];
  }, [
    baseCurrency,
    quoteCurrency,
    canViewCreditAndBalances,
    unifiedLiquidityColumns,
    selectedUnifiedLiquidityMarkets,
    onChangeUnifiedLiquidityMarkets,
  ]);

  return { columns, unifiedLiquidityColumns };
};

export interface UnifiedLiquidityColumn {
  id: string;
  group: UnifiedLiquidityColumnGroup;
  quoteCurrency: string;
  symbol: string;
  market: string;
}

export const UnifiedLiquidityColumnGroup = {
  Spot: 'SPOT',
  PerpCfd: 'PERP/CFD',
} as const;
export type UnifiedLiquidityColumnGroup =
  (typeof UnifiedLiquidityColumnGroup)[keyof typeof UnifiedLiquidityColumnGroup];

function getUnifiedLiquidityColumns(
  tokens: UnifiedLiquidityToken[],
  securitiesBySymbol: Map<string, Security>
): UnifiedLiquidityColumn[] {
  const columns = compact(
    tokens.map(token => {
      const security = securitiesBySymbol.get(token.Symbol);
      const group = security ? getUnifiedLiquidityColumnGroup(security) : undefined;
      if (!security?.QuoteCurrency || !group) {
        return undefined;
      }
      return {
        id: getUnifiedLiquidityColumnId(group, security.QuoteCurrency),
        group,
        quoteCurrency: security.QuoteCurrency,
        symbol: token.Symbol,
        market: token.Market,
      };
    })
  );

  return sortUnifiedLiquidityColumns(uniqBy(columns, column => column.id));
}

export function getUnifiedLiquidityColumnGroup(
  security: Security | undefined
): UnifiedLiquidityColumnGroup | undefined {
  if (!security) {
    return undefined;
  }

  switch (security.ProductType) {
    case ProductTypeEnum.Spot:
      return UnifiedLiquidityColumnGroup.Spot;
    case ProductTypeEnum.PerpetualSwap:
    case ProductTypeEnum.CFD:
    case ProductTypeEnum.Future:
    case ProductTypeEnum.Basis:
    case ProductTypeEnum.Option:
    case ProductTypeEnum.CalendarSpread:
    case ProductTypeEnum.Synthetic:
      return UnifiedLiquidityColumnGroup.PerpCfd;
    default:
      return undefined;
  }
}

function sortUnifiedLiquidityColumns(columns: UnifiedLiquidityColumn[]) {
  const groupOrder = new Map<UnifiedLiquidityColumnGroup, number>([
    [UnifiedLiquidityColumnGroup.Spot, 0],
    [UnifiedLiquidityColumnGroup.PerpCfd, 1],
  ]);
  return columns.toSorted((a, b) => {
    const groupCompare = (groupOrder.get(a.group) ?? 0) - (groupOrder.get(b.group) ?? 0);
    return groupCompare || a.quoteCurrency.localeCompare(b.quoteCurrency);
  });
}

function getUnifiedLiquidityColumnGroups({
  unifiedLiquidityColumns,
  selectedUnifiedLiquidityMarkets,
  onChangeUnifiedLiquidityMarkets,
}: {
  unifiedLiquidityColumns: UnifiedLiquidityColumn[];
  selectedUnifiedLiquidityMarkets: UnifiedLiquidityMarketSelection[];
  onChangeUnifiedLiquidityMarkets: (selections: UnifiedLiquidityMarketSelection[]) => void;
}): ColumnGroup[] {
  return Object.values(UnifiedLiquidityColumnGroup)
    .map(group => ({
      group,
      columns: unifiedLiquidityColumns.filter(column => column.group === group),
    }))
    .filter(({ columns }) => columns.length > 0)
    .map(({ group, columns }) => ({
      type: COLUMN_GROUP_NATIVE_TYPE,
      groupId: `ul-${group}`,
      headerName: group,
      children: columns.map(column => ({
        type: 'custom',
        field: column.id,
        title: column.quoteCurrency,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        params: {
          cellRenderer: (params: ICellRendererParams<MaoRow>) => {
            if (!params.data) {
              if (params.node.level !== 0) {
                return null;
              }

              const groupSelections = getUnifiedLiquidityColumnSelectionsForGroupRow(params, column.id);
              const selectedCount = groupSelections.filter(selection =>
                hasUnifiedLiquiditySelection(selectedUnifiedLiquidityMarkets, selection)
              ).length;
              const checked = groupSelections.length > 0 && selectedCount === groupSelections.length;
              const indeterminate = selectedCount > 0 && !checked;
              return (
                <Checkbox
                  data-testid={`ul-market-selection-markets-${column.id}`}
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={() => {
                    onChangeUnifiedLiquidityMarkets(
                      toggleUnifiedLiquiditySelections(selectedUnifiedLiquidityMarkets, groupSelections, !checked)
                    );
                  }}
                />
              );
            }

            const selections = params.data?.unifiedLiquidityMarketsByColumn?.[column.id] ?? [];
            if (!selections.length) {
              return '-';
            }
            const selectedCount = selections.filter(selection =>
              hasUnifiedLiquiditySelection(selectedUnifiedLiquidityMarkets, selection)
            ).length;
            const checked = selectedCount === selections.length;
            const indeterminate = selectedCount > 0 && !checked;
            const firstSelection = selections[0];
            return (
              <Checkbox
                data-testid={`ul-market-selection-${firstSelection.marketAccount}-${firstSelection.symbol}`}
                checked={checked}
                indeterminate={indeterminate}
                onChange={() => {
                  onChangeUnifiedLiquidityMarkets(
                    toggleUnifiedLiquiditySelections(selectedUnifiedLiquidityMarkets, selections, !checked)
                  );
                }}
              />
            );
          },
        },
      })),
    }));
}

function getUnifiedLiquidityColumnSelectionsForGroupRow(params: ICellRendererParams<MaoRow>, columnId: string) {
  return compact(
    params.node.allLeafChildren?.flatMap(childNode => childNode.data?.unifiedLiquidityMarketsByColumn?.[columnId] ?? [])
  );
}

export function getUnifiedLiquidityColumnId(group: UnifiedLiquidityColumnGroup, quoteCurrency: string) {
  return `ul-${group}-${quoteCurrency}`;
}
